/* eslint-disable no-console */
import {
  collection,
  addDoc,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  where,
  increment,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type {
  NewWorkspaceComment,
  NewWorkspaceItem,
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceComment,
} from '@/types/workspace';

const COLLECTION = 'workspaceItems';

function mapDoc(d: any): WorkspaceItem {
  return { id: d.id, ...d.data() } as WorkspaceItem;
}

async function addItem(item: NewWorkspaceItem): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...item,
    votes: item.votes ?? 0,
    createdAt: new Date(),
    updatedAt: new Date(),
    pinned: item.pinned ?? false,
  });
  return ref.id;
}

async function updateItem(id: string, data: Partial<NewWorkspaceItem>): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), { ...data, updatedAt: new Date() } as any);
}

async function deleteItem(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

async function getItems(type?: WorkspaceItemType): Promise<WorkspaceItem[]> {
  const base = collection(db, COLLECTION);
  const q = type
    ? query(base, where('type', '==', type), orderBy('updatedAt', 'desc'))
    : query(base, orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(mapDoc);
}

function subscribeItems(
  onUpdate: (items: WorkspaceItem[]) => void,
  type?: WorkspaceItemType,
) {
  const base = collection(db, COLLECTION);
  const q = type
    ? query(base, where('type', '==', type), orderBy('updatedAt', 'desc'))
    : query(base, orderBy('updatedAt', 'desc'));
  return onSnapshot(q, (snap) => {
    onUpdate(snap.docs.map(mapDoc));
  });
}

async function voteItem(id: string, delta: number): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), { votes: increment(delta) });
}

// Comments subcollection
async function addComment(itemId: string, comment: NewWorkspaceComment): Promise<void> {
  await addDoc(collection(db, `${COLLECTION}/${itemId}/comments`), {
    ...comment,
    createdAt: new Date(),
  });
}

async function getComments(itemId: string): Promise<WorkspaceComment[]> {
  const q = query(
    collection(db, `${COLLECTION}/${itemId}/comments`),
    orderBy('createdAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as WorkspaceComment));
}

async function getItemById(id: string): Promise<WorkspaceItem | null> {
  const ref = doc(db, COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as WorkspaceItem;
}

export default {
  addItem,
  updateItem,
  deleteItem,
  getItems,
  subscribeItems,
  voteItem,
  addComment,
  getComments,
  getItemById,
};
